/**
 * PolishVideoButton
 * -----------------
 * Drop-in button for any completed Kling video row. Opens a compact panel
 * letting the user pick a music vibe, type a headline + CTA, and trigger
 * server-side FFmpeg post-production. Polls the task until polished_url
 * appears, then surfaces a "Play polished" CTA.
 *
 * Token cost (3 tokens) is shown up-front. Ultimate users pay 0.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import VideoPlayerDialog from "./VideoPlayerDialog";
import { iosSave } from "../lib/iosSave";
import {
  Sparkles,
  Music,
  Wand2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Play,
  Download,
  Coins,
  Mic,
  Captions,
  Image as ImageIcon,
  Cpu,
  Sliders,
  Eye,
  FastForward,
  Trash2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLISH_COST    = 3;
const VOICEOVER_COST = 2;
const CAPTIONS_COST  = 1;
const AI_MUSIC_COST  = 3;
const CUTAWAYS_COST  = 1;
const SLOWMO_COST    = 1;

export function PolishVideoButton({ video, onPolished }) {
  const { token, user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [voices, setVoices] = useState([]);
  const [aiVibes, setAiVibes] = useState([]);
  const [musicKey, setMusicKey] = useState("trendy");
  const [headline, setHeadline] = useState("");
  const [cta, setCta] = useState("Shop now");
  const [brand, setBrand] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [polishState, setPolishState] = useState(video.polish_status || null);
  const [polishedUrl, setPolishedUrl] = useState(video.polished_url || null);
  const [polishError, setPolishError] = useState(video.polish_error || null);
  // Phase 2 — voiceover + captions
  const [enableVoiceover, setEnableVoiceover] = useState(false);
  const [voiceKey, setVoiceKey] = useState("");
  const [voiceoverScript, setVoiceoverScript] = useState("");
  const [enableCaptions, setEnableCaptions] = useState(false);
  const [scriptDrafting, setScriptDrafting] = useState(false);
  // Phase 3 — AI music + cutaways
  const [enableAiMusic, setEnableAiMusic] = useState(false);
  const [aiMusicVibe, setAiMusicVibe] = useState("ai-trendy");
  const [enableCutaways, setEnableCutaways] = useState(false);
  // Phase 4 — editor controls
  const [editorOpen, setEditorOpen]                 = useState(false);
  const [captionStyle, setCaptionStyle]             = useState("tiktok");
  const [captionPosition, setCaptionPosition]       = useState("bottom");
  const [headlinePosition, setHeadlinePosition]     = useState("top");
  const [ctaPosition, setCtaPosition]               = useState("bottom-right");
  const [cutawayTimestamps, setCutawayTimestamps]   = useState([30, 62, 85]);
  // Phase 5 — slow-motion segments
  const [enableSlowmo, setEnableSlowmo]             = useState(false);
  const [slowmoSegments, setSlowmoSegments]         = useState([{ start_pct: 30, end_pct: 45, factor: 2.0 }]);
  const [previewImg, setPreviewImg]                 = useState(null);
  const [previewLoading, setPreviewLoading]         = useState(false);
  const previewDebounceRef = useRef(null);
  const pollRef = useRef(null);

  const isUltimate = user?.subscription_tier === "ultimate";
  const isRetryFree = polishState === "failed";

  const totalCost = useMemo(
    () =>
      POLISH_COST +
      (enableVoiceover ? VOICEOVER_COST : 0) +
      (enableCaptions  ? CAPTIONS_COST  : 0) +
      (enableAiMusic   ? AI_MUSIC_COST  : 0) +
      (enableCutaways  ? CUTAWAYS_COST  : 0) +
      (enableSlowmo    ? SLOWMO_COST    : 0),
    [enableVoiceover, enableCaptions, enableAiMusic, enableCutaways, enableSlowmo]
  );

  // Load libraries once when the panel is first opened
  useEffect(() => {
    if (!open) return;
    if (tracks.length === 0) {
      axios
        .get(`${API}/api/video-post/music-library`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setTracks(r.data?.tracks || []))
        .catch(() => toast.error("Could not load music library"));
    }
    if (voices.length === 0) {
      axios
        .get(`${API}/api/video-post/voice-library`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setVoices(r.data?.voices || []))
        .catch(() => toast.error("Could not load voice library"));
    }
    if (aiVibes.length === 0) {
      axios
        .get(`${API}/api/video-post/ai-music-vibes`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setAiVibes(r.data?.vibes || []))
        .catch(() => { /* non-critical */ });
    }
  }, [open, tracks.length, voices.length, aiVibes.length, token]);

  // Captions require voiceover — keep them in sync. Use a ref so we don't
  // trip the react-hooks/set-state-in-effect rule.
  if (!enableVoiceover && enableCaptions) {
    // setState in render is fine when guarded — React will re-render once.
    setEnableCaptions(false);
  }

  // Stop any in-flight polling when the component unmounts
  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearTimeout(previewDebounceRef.current);
  }, []);

  // Phase 4 — live preview frame (debounced 600ms after the last edit)
  useEffect(() => {
    if (!open || !editorOpen) return;
    clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await axios.post(
          `${API}/api/video-post/${video.id}/preview-frame`,
          {
            headline,
            cta,
            enable_captions: enableCaptions,
            caption_style: captionStyle,
            caption_position: captionPosition,
            headline_position: headlinePosition,
            cta_position: ctaPosition,
            frame_at: 2.5,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPreviewImg(r.data?.image_b64 || null);
      } catch (e) {
        /* swallow — preview is best-effort */
      } finally {
        setPreviewLoading(false);
      }
    }, 600);
    return () => clearTimeout(previewDebounceRef.current);
  }, [
    open, editorOpen, headline, cta, enableCaptions,
    captionStyle, captionPosition, headlinePosition, ctaPosition,
    video.id, token,
  ]);

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/tasks/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fresh = (r.data?.tasks || []).find((t) => t.id === video.id);
        if (!fresh) return;
        if (fresh.polish_status && fresh.polish_status !== polishState) {
          setPolishState(fresh.polish_status);
        }
        if (fresh.polish_error) setPolishError(fresh.polish_error);
        if (fresh.polish_status === "completed" && fresh.polished_url) {
          setPolishedUrl(absUrl(fresh.polished_url));
          clearInterval(pollRef.current);
          toast.success("Polished video ready");
          refreshUser?.();
          onPolished?.(fresh);
        }
        if (fresh.polish_status === "failed") {
          clearInterval(pollRef.current);
          toast.error(fresh.polish_error || "Polishing failed — tokens refunded", { duration: 8000 });
          refreshUser?.();
        }
      } catch (e) {
        /* swallow: a single failed poll shouldn't break the loop */
      }
    }, 4000);
  };

  const handleDraftScript = async () => {
    setScriptDrafting(true);
    try {
      const r = await axios.post(
        `${API}/api/video-post/${video.id}/auto-script`,
        { headline, cta },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoiceoverScript(r.data?.script || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not draft script");
    } finally {
      setScriptDrafting(false);
    }
  };

  const handlePolish = async () => {
    if (enableVoiceover && !voiceKey) {
      toast.error("Pick a voice for the voiceover");
      return;
    }
    setSubmitting(true);
    setPolishError(null);
    try {
      await axios.post(
        `${API}/api/video-post/${video.id}/polish`,
        {
          headline,
          cta,
          brand,
          music_key: enableAiMusic ? null : (musicKey || null),
          music_volume: 0.55,
          keep_source_audio: true,
          enable_voiceover: enableVoiceover,
          voice_key: enableVoiceover ? voiceKey : null,
          voiceover_script: enableVoiceover ? (voiceoverScript || null) : null,
          enable_captions: enableCaptions,
          enable_ai_music: enableAiMusic,
          ai_music_vibe: enableAiMusic ? aiMusicVibe : null,
          enable_cutaways: enableCutaways,
          // Phase 4 — editor controls
          caption_style: captionStyle,
          caption_position: captionPosition,
          headline_position: headlinePosition,
          cta_position: ctaPosition,
          cutaway_timestamps: enableCutaways ? cutawayTimestamps : null,
          enable_slowmo: enableSlowmo,
          slowmo_segments: enableSlowmo ? slowmoSegments : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPolishState("processing");
      toast.success(
        isUltimate || isRetryFree
          ? "Polishing started (free)"
          : `Polishing started · −${totalCost} tokens`
      );
      refreshUser?.();
      startPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start polish");
    } finally {
      setSubmitting(false);
    }
  };

  const polishedPublicUrl = useMemo(() => absUrl(polishedUrl), [polishedUrl]);

  // ---- already polished: just show the result -------------------------------
  if (polishState === "completed" && polishedPublicUrl) {
    return (
      <div className="mt-3 p-3 rounded-2xl border border-citrus-500/40 bg-citrus-500/10" data-testid={`polish-result-${video.id}`}>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-ink-950" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">Polished</span>
        </div>
        <video src={polishedPublicUrl} controls className="w-full rounded-xl mb-2" />
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="default"
            className="flex-1"
            onClick={() => setPlayerOpen(true)}
            data-testid={`polish-play-${video.id}`}
          >
            <Play className="w-3.5 h-3.5" /> Play
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => iosSave(polishedPublicUrl, { kind: "video", filename: `xea-polished-${video.id}.mp4` })}
            data-testid={`polish-download-${video.id}`}
          >
            <Download className="w-3.5 h-3.5" /> Download
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setPolishState(null);
              setPolishedUrl(null);
              setOpen(true);
            }}
            data-testid={`polish-redo-${video.id}`}
          >
            Re-polish
          </Button>
        </div>
        <VideoPlayerDialog
          open={playerOpen}
          onClose={() => setPlayerOpen(false)}
          url={polishedPublicUrl}
          title={`Polished · ${video.prompt?.slice(0, 60) || "video"}`}
        />
      </div>
    );
  }

  // ---- in-flight ------------------------------------------------------------
  if (polishState === "processing") {
    return (
      <div className="mt-3 p-3 rounded-2xl border border-coral-500/30 bg-coral-500/10 flex items-center gap-3" data-testid={`polish-progress-${video.id}`}>
        <Loader2 className="w-4 h-4 text-coral-500 animate-spin" />
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">Polishing</p>
          <p className="text-xs text-ink-500">FFmpeg is mixing music, intro, outro & overlays… ~20s</p>
        </div>
      </div>
    );
  }

  // ---- failed ---------------------------------------------------------------
  if (polishState === "failed" && !open) {
    return (
      <div className="mt-3 p-3 rounded-2xl border border-danger/40 bg-danger/10" data-testid={`polish-failed-${video.id}`}>
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-4 h-4 text-danger" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-danger">Polish failed</span>
        </div>
        {polishError && <p className="text-xs text-ink-500 mb-2 line-clamp-2">{polishError}</p>}
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          Try again (free)
        </Button>
      </div>
    );
  }

  // ---- entry CTA + form -----------------------------------------------------
  return (
    <div className="mt-3" data-testid={`polish-block-${video.id}`}>
      {!open ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          className="w-full !border-citrus-500/60 !bg-citrus-500/10 hover:!bg-citrus-500/20 !text-ink-950"
          data-testid={`polish-open-${video.id}`}
        >
          <Sparkles className="w-3.5 h-3.5" /> Polish for socials
          <span className="ml-auto text-[10px] font-bold opacity-70">
            {isUltimate ? "FREE" : `from ${POLISH_COST} tokens`}
          </span>
        </Button>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden rounded-2xl border border-ink-200 bg-white p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-coral-500" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">Polish</span>
              </div>
              {!isUltimate && (
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500 flex items-center gap-1">
                  <Coins className="w-3 h-3 text-citrus-500" />
                  {isRetryFree ? "Free retry" : `${totalCost} tokens`}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Headline (auto if blank)"
                maxLength={80}
                data-testid={`polish-headline-${video.id}`}
              />
              <Input
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                placeholder="CTA"
                maxLength={28}
                data-testid={`polish-cta-${video.id}`}
              />
              <Input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Brand (auto if blank)"
                maxLength={28}
                className="col-span-2"
                data-testid={`polish-brand-${video.id}`}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500 flex items-center gap-1">
                  <Music className="w-3 h-3" /> Background music
                </p>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-coral-500 flex items-center gap-1">
                    <Cpu className="w-2.5 h-2.5" /> AI generate
                    {!isUltimate && <span className="text-ink-500">+{AI_MUSIC_COST}</span>}
                  </span>
                  <input
                    type="checkbox"
                    checked={enableAiMusic}
                    onChange={(e) => setEnableAiMusic(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-ink-300 text-coral-500 focus:ring-coral-500"
                    data-testid={`polish-toggle-aimusic-${video.id}`}
                  />
                </label>
              </div>

              {enableAiMusic ? (
                <div className="grid grid-cols-2 gap-2" data-testid={`polish-aimusic-grid-${video.id}`}>
                  {aiVibes.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setAiMusicVibe(v.key)}
                      className={`text-left p-2.5 rounded-xl border transition-all ${
                        aiMusicVibe === v.key
                          ? "border-coral-500 bg-coral-500/10"
                          : "border-ink-200 hover:border-ink-400"
                      }`}
                      data-testid={`polish-aimusic-${v.key}-${video.id}`}
                    >
                      <p className="text-xs font-bold text-ink-950">{v.label}</p>
                      <p className="text-[10px] leading-tight text-ink-500">{v.description}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {tracks.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setMusicKey(t.key)}
                      className={`text-left p-2.5 rounded-xl border transition-all ${
                        musicKey === t.key
                          ? "border-ink-950 bg-ink-950 text-white"
                          : "border-ink-200 hover:border-ink-400"
                      }`}
                      data-testid={`polish-music-${t.key}-${video.id}`}
                    >
                      <p className="text-xs font-bold">{t.label}</p>
                      <p className={`text-[10px] leading-tight ${musicKey === t.key ? "text-white/70" : "text-ink-500"}`}>
                        {t.description}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product image cutaways toggle */}
            <label className="flex items-center justify-between gap-3 cursor-pointer select-none rounded-xl border border-ink-200 p-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-3.5 h-3.5 text-coral-500" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">
                  Product cutaways
                  <span className="text-ink-500 normal-case font-medium ml-1">(2-3 beat-timed flashes)</span>
                </span>
                {!isUltimate && (
                  <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-ink-500 ml-1">
                    +{CUTAWAYS_COST}
                  </span>
                )}
              </div>
              <input
                type="checkbox"
                checked={enableCutaways}
                onChange={(e) => setEnableCutaways(e.target.checked)}
                className="w-4 h-4 rounded border-ink-300 text-ink-950 focus:ring-ink-950"
                data-testid={`polish-toggle-cutaways-${video.id}`}
              />
            </label>

            {/* Slow-motion toggle */}
            <label className="flex items-center justify-between gap-3 cursor-pointer select-none rounded-xl border border-ink-200 p-3">
              <div className="flex items-center gap-2">
                <FastForward className="w-3.5 h-3.5 text-coral-500 rotate-180" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">
                  Slow-motion
                  <span className="text-ink-500 normal-case font-medium ml-1">(highlight key moments)</span>
                </span>
                {!isUltimate && (
                  <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-ink-500 ml-1">
                    +{SLOWMO_COST}
                  </span>
                )}
              </div>
              <input
                type="checkbox"
                checked={enableSlowmo}
                onChange={(e) => setEnableSlowmo(e.target.checked)}
                className="w-4 h-4 rounded border-ink-300 text-ink-950 focus:ring-ink-950"
                data-testid={`polish-toggle-slowmo-${video.id}`}
              />
            </label>

            {/* Phase 4 — Editor (advanced controls + live preview) */}
            <div className="rounded-xl border border-ink-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setEditorOpen((v) => !v)}
                className="w-full flex items-center justify-between p-3 hover:bg-ink-50 transition-colors"
                data-testid={`polish-editor-toggle-${video.id}`}
              >
                <span className="flex items-center gap-2">
                  <Sliders className="w-3.5 h-3.5 text-coral-500" />
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">Editor</span>
                  <span className="text-[10px] font-medium text-ink-500 normal-case">
                    Position, style, timing & live preview
                  </span>
                </span>
                <span className={`text-ink-500 transition-transform ${editorOpen ? "rotate-90" : ""}`}>›</span>
              </button>

              <AnimatePresence>
                {editorOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden border-t border-ink-200 p-3 space-y-3 bg-ink-50/60"
                    data-testid={`polish-editor-panel-${video.id}`}
                  >
                    {/* Live preview */}
                    <div className="rounded-lg overflow-hidden bg-ink-950 aspect-[9/16] max-h-[280px] mx-auto flex items-center justify-center relative">
                      {previewImg ? (
                        <img
                          src={previewImg}
                          alt="Preview"
                          className="max-h-full max-w-full object-contain"
                          data-testid={`polish-preview-img-${video.id}`}
                        />
                      ) : (
                        <div className="text-ink-500 text-xs flex items-center gap-2">
                          <Eye className="w-3 h-3" /> Tweak fields below to see a preview…
                        </div>
                      )}
                      {previewLoading && (
                        <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                          <Loader2 className="w-3 h-3 text-white animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Headline position */}
                    <PositionRow
                      label="Headline position"
                      value={headlinePosition}
                      onChange={setHeadlinePosition}
                      options={[
                        { key: "top",    label: "Top" },
                        { key: "middle", label: "Middle" },
                        { key: "bottom", label: "Bottom" },
                      ]}
                      testId={`polish-headline-pos-${video.id}`}
                    />

                    {/* CTA position */}
                    <PositionRow
                      label="CTA chip position"
                      value={ctaPosition}
                      onChange={setCtaPosition}
                      options={[
                        { key: "top-left",     label: "TL" },
                        { key: "top-right",    label: "TR" },
                        { key: "bottom-left",  label: "BL" },
                        { key: "bottom-right", label: "BR" },
                      ]}
                      testId={`polish-cta-pos-${video.id}`}
                    />

                    {/* Caption controls (only when captions are enabled) */}
                    {enableCaptions && (
                      <>
                        <PositionRow
                          label="Caption style"
                          value={captionStyle}
                          onChange={setCaptionStyle}
                          options={[
                            { key: "tiktok", label: "TikTok" },
                            { key: "reels",  label: "Reels" },
                            { key: "subtle", label: "Subtle" },
                          ]}
                          testId={`polish-caption-style-${video.id}`}
                        />
                        <PositionRow
                          label="Caption position"
                          value={captionPosition}
                          onChange={setCaptionPosition}
                          options={[
                            { key: "top",    label: "Top" },
                            { key: "middle", label: "Middle" },
                            { key: "bottom", label: "Bottom" },
                          ]}
                          testId={`polish-caption-pos-${video.id}`}
                        />
                      </>
                    )}

                    {/* Cutaway timing sliders (only when cutaways are enabled) */}
                    {enableCutaways && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
                          Cutaway timing (% of video)
                        </p>
                        {cutawayTimestamps.map((stamp, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-ink-500 w-10">Cut {idx + 1}</span>
                            <input
                              type="range"
                              min="5"
                              max="95"
                              step="1"
                              value={stamp}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                const next = [...cutawayTimestamps];
                                next[idx] = v;
                                setCutawayTimestamps(next);
                              }}
                              className="flex-1 accent-coral-500"
                              data-testid={`polish-cutaway-slider-${idx}-${video.id}`}
                            />
                            <span className="text-[10px] font-bold text-ink-950 w-8 text-right">{stamp}%</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Slow-motion segment editor */}
                    {enableSlowmo && (
                      <div className="space-y-2" data-testid={`polish-slowmo-editor-${video.id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
                            Slow-motion segments
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              if (slowmoSegments.length >= 4) return;
                              const last = slowmoSegments[slowmoSegments.length - 1] || { end_pct: 20 };
                              const start = Math.min(95, (last.end_pct ?? 20) + 5);
                              const next = [
                                ...slowmoSegments,
                                { start_pct: start, end_pct: Math.min(98, start + 15), factor: 2.0 },
                              ];
                              setSlowmoSegments(next);
                            }}
                            disabled={slowmoSegments.length >= 4}
                            className="text-[10px] font-bold uppercase tracking-[0.18em] text-coral-500 hover:text-coral-600 disabled:opacity-40"
                            data-testid={`polish-slowmo-add-${video.id}`}
                          >
                            + Add segment
                          </button>
                        </div>
                        {slowmoSegments.length === 0 && (
                          <p className="text-[10px] text-ink-500 italic">
                            No segments. Add one to slow a moment.
                          </p>
                        )}
                        {slowmoSegments.map((seg, idx) => (
                          <div
                            key={idx}
                            className="rounded-lg bg-white border border-ink-200 p-2 space-y-1.5"
                            data-testid={`polish-slowmo-segment-${idx}-${video.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-ink-500">Segment {idx + 1}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const next = slowmoSegments.filter((_, i) => i !== idx);
                                  setSlowmoSegments(next);
                                }}
                                className="text-ink-400 hover:text-red-500"
                                data-testid={`polish-slowmo-remove-${idx}-${video.id}`}
                                aria-label="Remove segment"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-ink-500 w-10">Start</span>
                              <input
                                type="range" min="0" max="98" step="1"
                                value={seg.start_pct}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  const next = [...slowmoSegments];
                                  // Keep at least 2% gap to end
                                  next[idx] = { ...seg, start_pct: Math.min(v, (seg.end_pct ?? 100) - 2) };
                                  setSlowmoSegments(next);
                                }}
                                className="flex-1 accent-coral-500"
                                data-testid={`polish-slowmo-start-${idx}-${video.id}`}
                              />
                              <span className="text-[10px] font-bold text-ink-950 w-8 text-right">{seg.start_pct}%</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-ink-500 w-10">End</span>
                              <input
                                type="range" min="2" max="100" step="1"
                                value={seg.end_pct}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  const next = [...slowmoSegments];
                                  next[idx] = { ...seg, end_pct: Math.max(v, (seg.start_pct ?? 0) + 2) };
                                  setSlowmoSegments(next);
                                }}
                                className="flex-1 accent-coral-500"
                                data-testid={`polish-slowmo-end-${idx}-${video.id}`}
                              />
                              <span className="text-[10px] font-bold text-ink-950 w-8 text-right">{seg.end_pct}%</span>
                            </div>
                            <div className="flex items-center gap-2 pt-0.5">
                              <span className="text-[10px] font-bold text-ink-500 w-10">Speed</span>
                              <div className="flex-1 grid grid-cols-3 gap-1">
                                {[1.5, 2.0, 3.0].map((f) => (
                                  <button
                                    key={f}
                                    type="button"
                                    onClick={() => {
                                      const next = [...slowmoSegments];
                                      next[idx] = { ...seg, factor: f };
                                      setSlowmoSegments(next);
                                    }}
                                    className={`text-[10px] font-bold py-1 rounded-md transition-all ${
                                      seg.factor === f
                                        ? "bg-ink-950 text-white"
                                        : "bg-ink-100 text-ink-500 hover:text-ink-950"
                                    }`}
                                    data-testid={`polish-slowmo-factor-${idx}-${f}-${video.id}`}
                                  >
                                    {f}× slower
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Voiceover toggle + voice picker */}
            <div className="rounded-xl border border-ink-200 p-3 space-y-3">
              <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                <div className="flex items-center gap-2">
                  <Mic className="w-3.5 h-3.5 text-coral-500" />
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">AI voiceover</span>
                  {!isUltimate && (
                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-ink-500 ml-1">
                      +{VOICEOVER_COST} tokens
                    </span>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={enableVoiceover}
                  onChange={(e) => setEnableVoiceover(e.target.checked)}
                  className="w-4 h-4 rounded border-ink-300 text-ink-950 focus:ring-ink-950"
                  data-testid={`polish-toggle-voiceover-${video.id}`}
                />
              </label>

              <AnimatePresence>
                {enableVoiceover && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden space-y-2"
                  >
                    <div className="grid grid-cols-2 gap-1.5">
                      {voices.map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => setVoiceKey(v.key)}
                          className={`text-left p-2 rounded-lg border transition-all ${
                            voiceKey === v.key
                              ? "border-coral-500 bg-coral-500/10"
                              : "border-ink-200 hover:border-ink-400"
                          }`}
                          data-testid={`polish-voice-${v.key}-${video.id}`}
                        >
                          <p className="text-[11px] font-bold text-ink-950">{v.label}</p>
                          <p className="text-[9px] leading-tight text-ink-500 line-clamp-2">{v.description}</p>
                        </button>
                      ))}
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">Script</span>
                        <button
                          type="button"
                          onClick={handleDraftScript}
                          disabled={scriptDrafting}
                          className="text-[10px] font-bold uppercase tracking-[0.18em] text-coral-500 hover:text-coral-600 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`polish-draft-script-${video.id}`}
                        >
                          {scriptDrafting ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Wand2 className="w-2.5 h-2.5" />}
                          Auto-draft
                        </button>
                      </div>
                      <textarea
                        value={voiceoverScript}
                        onChange={(e) => setVoiceoverScript(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="Voiceover script (auto-generated if blank)…"
                        className="w-full rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-950 transition-all focus:outline-none focus:bg-white focus:border-ink-950"
                        data-testid={`polish-script-${video.id}`}
                      />
                    </div>

                    {/* Captions toggle — only visible when voiceover is on */}
                    <label className="flex items-center justify-between gap-3 cursor-pointer select-none pt-1">
                      <div className="flex items-center gap-2">
                        <Captions className="w-3.5 h-3.5 text-coral-500" />
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">
                          Captions <span className="text-ink-500 normal-case font-medium">(word-by-word burn-in)</span>
                        </span>
                        {!isUltimate && (
                          <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-ink-500">
                            +{CAPTIONS_COST}
                          </span>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={enableCaptions}
                        onChange={(e) => setEnableCaptions(e.target.checked)}
                        className="w-4 h-4 rounded border-ink-300 text-ink-950 focus:ring-ink-950"
                        data-testid={`polish-toggle-captions-${video.id}`}
                      />
                    </label>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={handlePolish}
                disabled={submitting}
                className="flex-1"
                data-testid={`polish-submit-${video.id}`}
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {submitting ? "Starting…" : "Polish video"}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

// Helper — prefix /api/uploads/* with the backend URL when the server returns a
// root-relative path. External Kling URLs are returned as-is.
function absUrl(p) {
  if (!p) return p;
  if (/^https?:\/\//.test(p)) return p;
  return `${API}${p}`;
}


// Compact segmented control used throughout the editor section.
function PositionRow({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">{label}</p>
      <div className="grid grid-flow-col auto-cols-fr gap-1 rounded-lg bg-ink-100 p-1" data-testid={testId}>
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`text-[10px] font-bold uppercase tracking-[0.10em] py-1.5 rounded-md transition-all ${
              value === opt.key
                ? "bg-white text-ink-950 shadow-sm"
                : "text-ink-500 hover:text-ink-950"
            }`}
            data-testid={`${testId}-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
